
import React from "react";
import { MapPin, Phone, Users, Calendar, Clock, User, Mail, Globe, Bed, CheckCircle, XCircle, Video, FileText } from "lucide-react";
import { useMediaQuery } from "@/hooks/use-media-query";

interface QuickFactsProps {
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  manager?: string;
  personInCharge?: string;
  maxResidents?: number;
  yearEstablished?: number;
  visitingHours?: string;
  availableBeds?: number | null;
  acceptingEnquiries?: boolean;
  virtualTourUrl?: string;
  brochureUrl?: string;
}

export function QuickFacts({ 
  address, 
  phone, 
  email,
  website,
  manager, 
  personInCharge,
  maxResidents,
  yearEstablished,
  visitingHours = "10:00 AM - 8:00 PM",
  availableBeds,
  acceptingEnquiries,
  virtualTourUrl,
  brochureUrl,
}: QuickFactsProps) {
  const isMobile = useMediaQuery("(max-width: 640px)");
  
  return (
    <div className="space-y-6">
      {/* Availability banner */}
      {(availableBeds != null || acceptingEnquiries !== undefined) && (
        <div className={`rounded-lg p-4 border ${acceptingEnquiries !== false ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex items-center gap-3">
            {acceptingEnquiries !== false ? (
              <CheckCircle className="h-5 w-5 text-green-600 shrink-0" />
            ) : (
              <XCircle className="h-5 w-5 text-amber-600 shrink-0" />
            )}
            <div>
              <p className={`font-semibold ${acceptingEnquiries !== false ? 'text-green-800' : 'text-amber-800'}`}>
                {acceptingEnquiries !== false
                  ? availableBeds != null && availableBeds > 0
                    ? `${availableBeds} bed${availableBeds === 1 ? '' : 's'} available`
                    : 'Accepting enquiries'
                  : 'Currently at capacity'}
              </p>
              {acceptingEnquiries !== false && availableBeds != null && availableBeds > 0 && (
                <p className="text-sm text-green-700">Contact us to arrange a visit</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action buttons row */}
      {(virtualTourUrl || brochureUrl) && (
        <div className="flex flex-wrap gap-3">
          {virtualTourUrl && (
            <a
              href={virtualTourUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-grandpal-green text-white hover:bg-grandpal-green/90 transition-colors text-sm font-medium"
            >
              <Video className="h-4 w-4" />
              Take a Virtual Tour
            </a>
          )}
          {brochureUrl && (
            <a
              href={brochureUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <FileText className="h-4 w-4" />
              Download Brochure
            </a>
          )}
        </div>
      )}

      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2">
      {address && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <MapPin className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Address</span>
            <span className="text-sm text-gray-700">{address}</span>
          </div>
        </div>
      )}
      
      {phone && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Phone className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Phone</span>
            <a 
              href={`tel:${phone.replace(/\D/g, '')}`} 
              className="text-sm text-grandpal-gray hover:text-grandpal-green py-0.5"
            >
              {phone}
            </a>
          </div>
        </div>
      )}
      
      {email && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Mail className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Email</span>
            <a 
              href={`mailto:${email}`} 
              className="text-sm text-grandpal-gray hover:text-grandpal-green truncate py-0.5"
            >
              {email}
            </a>
          </div>
        </div>
      )}
      
      {website && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Globe className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Website</span>
            <a 
              href={website.startsWith('http') ? website : `https://${website}`}
              target="_blank"
              rel="noopener noreferrer" 
              className="text-sm text-grandpal-gray hover:text-grandpal-green truncate py-0.5"
            >
              {website.replace(/^https?:\/\//, '')}
            </a>
          </div>
        </div>
      )}
      
      {personInCharge && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <User className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Person in charge</span>
            <span className="text-sm text-gray-700">{personInCharge}</span>
          </div>
        </div>
      )}
      
      {manager && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <User className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Managed by</span>
            <span className="text-sm text-gray-700">{manager}</span>
          </div>
        </div>
      )}
      
      {maxResidents && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Bed className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Capacity</span>
            <span className="text-sm text-gray-700">{maxResidents} beds</span>
          </div>
        </div>
      )}
      
      {yearEstablished && (
        <div className="flex items-start gap-3">
          <div className="mt-1 flex-shrink-0">
            <Calendar className="h-4 w-4 text-grandpal-green" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-medium">Established</span>
            <span className="text-sm text-gray-700">{yearEstablished}</span>
          </div>
        </div>
      )}
      
      <div className="flex items-start gap-3">
        <div className="mt-1 flex-shrink-0">
          <Clock className="h-4 w-4 text-grandpal-green" />
        </div>
        <div className="flex flex-col">
          <span className="text-sm font-medium">Visiting hours</span>
          <span className="text-sm text-gray-700">{visitingHours}</span>
        </div>
      </div>
      </div>
    </div>
  );
}
